import { Button, Classes, Icon, InputGroup } from '@blueprintjs/core'
import { IconNames } from '@blueprintjs/icons'
import classNames from 'classnames'
import _ from 'lodash'
import React, { useEffect, useState } from 'react'

import { FramesManager } from 'shared-libs/components/view/frames-manager'
import { Entity } from 'shared-libs/models/entity'

import { IBaseProps } from 'browser/components/atomic-elements/atoms/base-props'
import { FormGroup } from 'browser/components/atomic-elements/atoms/form-group/form-group'
import { FormTable } from 'browser/components/atomic-elements/atoms/form-table/form-table'
import { Label } from 'browser/components/atomic-elements/atoms/label/label'
import 'browser/components/json-elements/atoms/_dictionary.scss'

const KEY_COL_WIDTH = 200
const REMOVE_COL_WIDTH = 30

const KeyInput: React.FC<{
  value: string
  isDisabled?: boolean
  onCommit: (value: string) => boolean
}> = ({ value, isDisabled, onCommit }) => {
  const [text, setText] = useState(value)
  useEffect(() => setText(value), [value])
  return (
    <InputGroup
      fill
      value={text}
      disabled={isDisabled}
      onChange={(e) => setText(e.currentTarget.value)}
      onBlur={() => {
        if (!onCommit(text.trim())) {
          setText(value)
        }
      }}
    />
  )
}

/**
 * @uiComponent
 *
 * UI component to render oject map (`{ [key]: value }`) — similar to `ui:list`.
 *
 * With no `entryTemplate` it renders a plain string→key / string→value editor. Supply an
 * `entryTemplate` (a single uiSchema node describing one entry) to control the layout yourself,
 * placing the key and value in the `ui:dictionaryKey` / `ui:dictionaryValue` containers.
 *
 * Example — a fully custom string→string entry:
 * {
 *   "type": "ui:dictionary",
 *   "value": "core_integrations_ingestion_multiEntity_payloadConfig.entities",
 *   "keyLabel": "Entity alias",
 *   "valueLabel": "Entity Spec",
 *   "entryTemplate": {
 *     "type": "ui:formTable",
 *     "className": "c-dictionaryEntry",
 *     "children": [
 *       {
 *         "type": "ui:dictionaryKey",
 *         "style": { "width": 200 },
 *         "itemTemplate": { "type": "ui:input", "value": ".", "placeholder": "Enter key" }
 *       },
 *       {
 *         "type": "ui:dictionaryValue",
 *         "style": { "width": 400 },
 *         "itemTemplate": { "type": "ui:input", "value": ".", "placeholder": "Enter value" }
 *       }
 *     ]
 *   }
 * }
 */
export interface IUIDictionaryProps extends IBaseProps {
  value?: Record<string, any>
  onChange: (value: any) => void
  entity: Entity
  frames: FramesManager
  isDisabled?: boolean
  errors?: any[]
  /** Column header for the key. Used only when no custom `header` is supplied (default two-column layout). */
  keyLabel?: string
  /** Column header for the value. Used only when no custom `header` is supplied (default two-column layout). */
  valueLabel?: string
  /**
   * Fully custom header uiSchema element, rendered above the rows and taking precedence over
   * `keyLabel`/`valueLabel`. Reuse the entry's `c-dictionaryKey`/`c-dictionaryValue` cells so it aligns
   * by construction. Read from the uiSchema (not a real React prop); declared here for documentation.
   */
  header?: any
  /** Default mode: sub-path within each value to edit as text (e.g. "entityId"); omit for scalar values where `map[key]` is the text. */
  valuePath?: string
  /** Placeholder for the value input (default mode only). */
  valuePlaceholder?: string
  /** Text on the add button (default "Add Item"). */
  addButtonText?: string
  /** Seed value for a newly-added entry; overrides the schema default. */
  createNewItem?: () => any
}

export class UIDictionary extends React.Component<IUIDictionaryProps> {
  public static defaultProps: Partial<IUIDictionaryProps> = {
    addButtonText: 'Add Item',
  }

  public render() {
    const { addButtonText } = this.props
    const entries = this.getEntries()
    const hasCustomEntry = !_.isNil(this.getEntryTemplateUiSchema())
    return (
      <div className="c-dictionary">
        {!_.isEmpty(entries) && this.renderHeader()}
        {entries.map((key) => this.renderRow(key, hasCustomEntry))}
        <Button
          className={classNames('c-abstractList-addButton', Classes.MINIMAL)}
          icon="add"
          small
          disabled={this.isDisabled()}
          onClick={this.handleAddItem}
        >
          {addButtonText}
        </Button>
      </div>
    )
  }

  private renderHeader() {
    const uiSchema =  this.props.frames.getContext('uiSchema').header
    if (!_.isNil(uiSchema)) {
      return this.renderCustomHeader(uiSchema)
    }
    const { keyLabel, valueLabel } = this.props
    if (!keyLabel || !valueLabel) {
      return null
    }
    return (
      <div
        className="u-flex u-alignItemsCenter c-placeholderColor u-bumperBottom--xs"
        style={{ gap: 8 }}
      >
        <div style={{ width: KEY_COL_WIDTH }}>{keyLabel}</div>
        <div style={{ flex: 1 }}>{valueLabel}</div>
        <div style={{ width: REMOVE_COL_WIDTH }} />
      </div>
    )
  }

  private renderCustomHeader(uiSchema: any) {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    if (!renderer) {
      return null
    }
    const uiSchemaPath = frames.getContext('uiSchemaPath').concat('header')
    const newFrames = renderer.createChildFrame(frames, { uiSchema, uiSchemaPath })
    return renderer.createElementFromFrame(newFrames, {})
  }

  private renderRow(key: string, hasCustomEntry: boolean) {
    return (
      <div key={key} className="c-dictionaryRow u-bumperBottom--sm">
        <div className="c-dictionaryRow-content">
          {hasCustomEntry ? this.renderCustomEntry(key) : this.renderDefaultEntry(key)}
        </div>
        {!this.isDisabled() && (
          <Button
            className={classNames('c-abstractListItem-removeButton', Classes.MINIMAL)}
            onClick={() => this.handleRemoveItem(key)}
          >
            <Icon icon={IconNames.CROSS} />
          </Button>
        )}
      </div>
    )
  }

  private renderDefaultEntry(key: string) {
    const { valuePath, valuePlaceholder } = this.props
    const isDisabled = this.isDisabled()
    const isUneditableObject = !valuePath && _.isObject(_.get(this.props.value, [key]))
    return (
      <FormTable className="c-dictionaryEntry">
        <div className="c-dictionaryKey" style={{ width: KEY_COL_WIDTH }}>
          {this.renderKey(key)}
        </div>
        <div className="c-dictionaryValue">
          <InputGroup
            fill
            placeholder={
              isUneditableObject
                ? 'Object value — set "valuePath" to edit a field'
                : valuePlaceholder
            }
            value={this.getDefaultValueText(key)}
            disabled={isDisabled || isUneditableObject}
            onChange={(e) => this.handleDefaultValueChange(key, e.currentTarget.value)}
          />
        </div>
      </FormTable>
    )
  }

  private getDefaultValueText(key: string): string {
    const { valuePath } = this.props
    const entry = _.get(this.props.value, [key])
    const raw = valuePath ? _.get(entry, valuePath) : entry
    if (_.isObject(raw)) {
      return '' // never '[object Object]'
    }
    return _.isNil(raw) ? '' : String(raw)
  }

  private renderCustomEntry(key: string) {
    return createDictionaryEntryElement({
      frames: this.props.frames,
      entryKey: key,
      entryValue: _.get(this.props.value, [key]),
      isDisabled: this.isDisabled(),
      onRenameKey: (newKey: string) => this.handleKeyChange(key, newKey),
    })
  }

  private renderKey(key: string) {
    return (
      <KeyInput
        value={key}
        isDisabled={this.isDisabled()}
        onCommit={(newKey: string) => this.handleKeyChange(key, newKey)}
      />
    )
  }

  private isDisabled(): boolean {
    return Boolean(this.props.isDisabled)
  }

  private getEntries(): string[] {
    return _.keys(this.props.value || {})
  }

  private getEntryTemplateUiSchema(): any {
    return this.props.frames.getContext('uiSchema').entryTemplate
  }

  private handleAddItem = () => {
    const { createNewItem } = this.props
    const used = new Set(this.getEntries())
    const newKey = this.nextEnumKey(used) ?? this.nextPlaceholderKey(used)
    const newValue =
      typeof createNewItem === 'function' ? createNewItem() : this.defaultEntryValue()
    this.commit({ ...this.props.value, [newKey]: newValue })
  }

  private nextEnumKey(used: Set<string>): string | undefined {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    const keySchema =
      !_.isEmpty(dataSchemaPath) && renderer
        ? renderer.getComponentDataSchema(dataSchemaPath.concat('propertyNames'))
        : undefined
    const enumValues: string[] = keySchema?.enum || []
    return enumValues.find((v) => !used.has(v))
  }

  private nextPlaceholderKey(used: Set<string>): string {
    let candidate = 'newKey'
    let i = 2
    while (used.has(candidate)) {
      candidate = `newKey${i++}`
    }
    return candidate
  }

  private handleRemoveItem(key: string) {
    this.commit(_.omit(this.props.value || {}, [key]))
  }

  private handleDefaultValueChange(key: string, text: string) {
    const { valuePath } = this.props
    const value = this.props.value || {}
    // Whole-map rewrite so numeric-string keys stay strings, not array indices.
    const entryValue = valuePath
      ? _.setWith(_.cloneDeep(value[key]) || {}, valuePath, text, Object)
      : text
    this.commit({ ...value, [key]: entryValue })
  }

  /** Returns false (caller reverts) if the new key is empty, unchanged, or already taken. */
  private handleKeyChange(oldKey: string, newKey: string): boolean {
    if (!newKey || newKey === oldKey || _.has(this.props.value, [newKey])) {
      return false
    }
    const next: Record<string, any> = {}
    _.forEach(this.props.value || {}, (v, k) => {
      next[k === oldKey ? newKey : k] = v
    })
    this.commit(next)
    return true
  }

  private commit(value: Record<string, any>) {
    this.props.onChange(value)
  }

  private defaultEntryValue(): any {
    const { frames } = this.props
    const renderer = frames.getContext('renderer')
    const dataSchemaPath = frames.getContext('dataSchemaPath')
    const valueSchema =
      !_.isEmpty(dataSchemaPath) && renderer
        ? renderer.getComponentDataSchema(dataSchemaPath.concat('additionalProperties'))
        : undefined
    return _.has(valueSchema, 'default') ? _.cloneDeep(valueSchema.default) : undefined
  }
}

/**
 * @uiComponent
 *
 * Key control for a `ui:dictionary` custom entry; reads the key + rename handler from frame context.
 * With an `itemTemplate` it renders your scalar control (enum keys commit the rename immediately,
 * free-text keys on blur); without one, a plain-string key input.
 */
export const UIDictionaryKey: React.FC<any> = ({
  frames,
  className,
  style,
  label,
  itemTemplate,
}) => {
  const entryKey = frames.getContext('dictionaryEntryKey')
  const onRename = frames.getContext('renameDictionaryKey')
  const isDisabled = frames.getContext('dictionaryEntryDisabled')

  // Draft the pending key locally so a free-text edit doesn't rewrite the whole map on every keystroke.
  const [draftKey, setDraftKey] = useState(entryKey)
  useEffect(() => setDraftKey(entryKey), [entryKey])

  if (!_.isNil(itemTemplate)) {
    // A closed-set control emits a valid key on every change, so commit immediately (vs on blur).
    const renderer = frames.getContext('renderer')
    const keyDataSchemaPath = frames.getContext('dictionaryKeyDataSchemaPath')
    const keySchema =
      !_.isEmpty(keyDataSchemaPath) && renderer?.getComponentDataSchema(keyDataSchemaPath)
    const isEnumKey = !_.isEmpty(keySchema?.enum)

    const commit = (candidate: string) => {
      const next = (candidate || '').trim()
      if (!next || !onRename || !onRename(next)) {
        setDraftKey(entryKey)
      }
    }
    const handleChange = (newKey: string) => {
      setDraftKey(newKey)
      // A select commits on menu-close; commit here with the fresh value rather than racing onBlur.
      if (isEnumKey) {
        commit(newKey)
      }
    }
    return (
      <div
        className={className}
        style={style}
        onBlur={isEnumKey ? undefined : () => commit(draftKey)}
      >
        {createDictionaryChildElement({
          frames,
          // Strip any `value` binding so the renderer doesn't build a path-bound onChange over ours.
          template: _.omit(itemTemplate, 'value'),
          isDisabled,
          dataSchemaPath: keyDataSchemaPath,
          defaultProps: {
            value: draftKey,
            onChange: handleChange,
          },
        })}
      </div>
    )
  }

  const control = (
    <KeyInput
      value={entryKey}
      isDisabled={isDisabled}
      onCommit={(newKey) => (onRename ? onRename(newKey) : false)}
    />
  )
  if (!label) {
    return (
      <div className={className} style={style}>
        {control}
      </div>
    )
  }
  return (
    <FormGroup className={className} isHorizontalLayout>
      <Label isHorizontalLayout text={label} />
      <div className="c-formGroup-horizontalContent">{control}</div>
    </FormGroup>
  )
}

/**
 * @uiComponent
 *
 * Value control for a `ui:dictionary` custom entry — the symmetric partner of `ui:dictionaryKey`.
 * Renders its `itemTemplate` (or inline `children`) bound to the entry value at `map[key]`, so
 * children bind normally.
 */
export const UIDictionaryValue: React.FC<any> = ({
  frames,
  className,
  style,
  itemTemplate,
  children,
}) => {
  return (
    <div className={className} style={style}>
      {_.isNil(itemTemplate)
        ? children
        : createDictionaryChildElement({ frames, template: itemTemplate })}
    </div>
  )
}

/** Renders a `ui:dictionaryKey` / `ui:dictionaryValue` child `template` through the renderer. */
export function createDictionaryChildElement(props): React.ReactElement<any> | null {
  const { frames, template, defaultProps, isDisabled, dataSchemaPath } = props
  const renderer = frames.getContext('renderer')
  if (!template || !renderer) {
    return null
  }
  const uiSchemaPath = frames.getContext('uiSchemaPath').concat('itemTemplate')
  const context: any = {
    uiSchema: template,
    uiSchemaPath,
    dictionaryEntryDisabled: isDisabled,
  }
  if (!_.isEmpty(dataSchemaPath)) {
    context.dataSchemaPath = dataSchemaPath
    // Resolve the schema explicitly: the key control has no `value` binding for the renderer to
    // derive it from, so without this a `ui:select` key gets no enum options.
    context.dataSchema = renderer.getComponentDataSchema(dataSchemaPath)
  }
  const newFrames = renderer.createChildFrame(frames, context)
  return renderer.createElementFromFrame(newFrames, defaultProps || {})
}

/** Renders one custom entry (the map analog of `ui:list`'s createListItemElement). */
function createDictionaryEntryElement(props: {
  frames: FramesManager
  entryKey: string
  entryValue: any
  isDisabled: boolean
  onRenameKey: (newKey: string) => boolean
}) {
  const { frames, entryKey, entryValue, isDisabled, onRenameKey } = props
  const renderer = frames.getContext('renderer')
  const uiSchema = frames.getContext('uiSchema').entryTemplate
  if (!uiSchema) {
    return null
  }
  const uiSchemaPath = frames.getContext('uiSchemaPath').concat('entryTemplate')
  const dictDataSchemaPath = frames.getContext('dataSchemaPath')
  const dictDataSchema = frames.getContext('dataSchema')
  let dataSchema = dictDataSchema
  let dataSchemaPath = dictDataSchemaPath
  let valuePath
  let keyDataSchemaPath
  if (!_.isEmpty(dictDataSchemaPath)) {
    if (dictDataSchema?.propertyNames) {
      keyDataSchemaPath = dictDataSchemaPath.concat('propertyNames')
    }
    dataSchemaPath = dictDataSchemaPath.concat('additionalProperties')
    dataSchema = renderer.getComponentDataSchema(dataSchemaPath)
    valuePath = frames.getContext('valuePath')?.concat(entryKey) ?? [entryKey]
  }
  const context = {
    dataSchema,
    dataSchemaPath,
    uiSchema,
    uiSchemaPath,
    valuePath,
    dictionaryEntryKey: entryKey,
    dictionaryKeyDataSchemaPath: keyDataSchemaPath,
    dictionaryEntryDisabled: isDisabled,
    renameDictionaryKey: onRenameKey,
  }
  const state = [entryValue, { key: entryKey, value: entryValue }]
  const newFrames = renderer.createChildFrame(frames, context, state)
  return renderer.createElementFromFrame(newFrames, {})
}
